import { useCallback, useEffect, useState } from 'react';
import { BookmarkPlus, Eye, GitCompare, Undo2, X } from 'lucide-react';
import type { DiffItem, HistoryEntrySummary, HistoryTrigger } from '@bits2schema/shared';
import { api } from '../api/client';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/types';
import { DIFF_CHANGE_TYPE_KEYS, DIFF_OBJECT_TYPE_KEYS } from '../utils/diffLabels';
import { describeError, formatDateTime } from '../utils/format';
import { Modal } from './Modal';
import { SqlPreview } from './SqlPreview';

interface HistoryPanelProps {
  projectId: string;
  onClose: () => void;
  /** Po úspěšném revertu se musí znovu načíst projekt (schema i canvas). */
  onReverted: () => void;
  /** Epic 14 (E14-US10) — checkpoint a revert jsou zápisové akce vyžadující edit lease; server je stejně vynutí (409). */
  readOnly?: boolean;
}

const TRIGGER_KEYS: Record<HistoryTrigger, MessageKey> = {
  checkpoint: 'history.trigger.checkpoint',
  dbSync: 'history.trigger.dbSync',
  changeScriptGenerated: 'history.trigger.changeScriptGenerated',
  revert: 'history.trigger.revert',
};

/** E3-US5/US6/US7 — historie verzí: seznam, checkpoint, diff mezi verzemi, revert. */
export function HistoryPanel({ projectId, onClose, onReverted, readOnly }: HistoryPanelProps) {
  const { t, language } = useI18n();
  const [entries, setEntries] = useState<HistoryEntrySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [checkpointLabel, setCheckpointLabel] = useState('');
  const [creatingCheckpoint, setCreatingCheckpoint] = useState(false);

  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [diff, setDiff] = useState<{ diff: DiffItem[] } | null>(null);
  const [diffBusy, setDiffBusy] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  const [pendingRevert, setPendingRevert] = useState<HistoryEntrySummary | null>(null);
  const [revertBusy, setRevertBusy] = useState(false);

  // Epic 9, E9-US6 — zpětné zobrazení skriptu uloženého u `changeScriptGenerated` záznamu.
  const [viewingScript, setViewingScript] = useState<{ entry: HistoryEntrySummary; script: string | null; error: string | null } | null>(
    null,
  );

  const load = useCallback(async () => {
    try {
      const { entries: list } = await api.getHistory(projectId);
      const chronological = [...list].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      setEntries(chronological);
      setError(null);
      if (chronological.length >= 2 && !fromId && !toId) {
        setToId(chronological[0].id);
        setFromId(chronological[1].id);
      }
    } catch (caught) {
      setError(describeError(caught, t));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const createCheckpoint = async () => {
    setCreatingCheckpoint(true);
    setError(null);
    try {
      await api.createCheckpoint(projectId, checkpointLabel.trim() || undefined);
      setCheckpointLabel('');
      await load();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setCreatingCheckpoint(false);
    }
  };

  const showDiff = async () => {
    if (!fromId || !toId) return;
    setDiffBusy(true);
    setDiffError(null);
    try {
      const result = await api.getHistoryDiff(projectId, fromId, toId);
      setDiff(result);
    } catch (caught) {
      setDiffError(describeError(caught, t));
    } finally {
      setDiffBusy(false);
    }
  };

  const confirmRevert = async () => {
    if (!pendingRevert) return;
    setRevertBusy(true);
    try {
      await api.revertHistoryEntry(projectId, pendingRevert.id);
      setPendingRevert(null);
      onReverted();
      await load();
    } catch (caught) {
      setError(describeError(caught, t));
      setPendingRevert(null);
    } finally {
      setRevertBusy(false);
    }
  };

  const viewScript = async (entry: HistoryEntrySummary) => {
    setViewingScript({ entry, script: null, error: null });
    try {
      const { script } = await api.getChangeScript(projectId, entry.id);
      setViewingScript({ entry, script, error: null });
    } catch (caught) {
      setViewingScript({ entry, script: null, error: describeError(caught, t) });
    }
  };

  return (
    <Modal
      title={t('history.title')}
      onClose={onClose}
      wide
      actions={<button type="button" onClick={onClose}><X size={16} />{t('common.close')}</button>}
    >
      <div className="stack history-panel">
        {error && <div className="notice notice--error">{error}</div>}

        <div className="row">
          <div className="field">
            <label htmlFor="history-checkpoint-label">{t('history.checkpointLabelPlaceholder')}</label>
            <input
              id="history-checkpoint-label"
              value={checkpointLabel}
              onChange={(event) => setCheckpointLabel(event.target.value)}
              placeholder={t('history.checkpointLabelPlaceholder')}
            />
          </div>
          <button type="button" className="primary" disabled={creatingCheckpoint || readOnly} onClick={() => void createCheckpoint()}>
            <BookmarkPlus size={16} />
            {t('history.createCheckpoint')}
          </button>
        </div>

        {entries === null ? (
          <p className="hint">{t('common.loading')}</p>
        ) : entries.length === 0 ? (
          <p className="hint">{t('history.empty')}</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>{t('history.column.trigger')}</th>
                <th>{t('history.column.time')}</th>
                <th>{t('history.column.author')}</th>
                <th>{t('history.column.label')}</th>
                <th className="numeric">{t('history.column.tables')}</th>
                <th>{t('history.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <span className="badge">{t(TRIGGER_KEYS[entry.trigger])}</span>
                  </td>
                  <td>{formatDateTime(entry.timestamp, t, language)}</td>
                  <td>{entry.authorDisplayName}</td>
                  <td>{entry.label ?? '—'}</td>
                  <td className="numeric">{entry.tableCount}</td>
                  <td>
                    {entry.trigger === 'changeScriptGenerated' && (
                      <button type="button" onClick={() => void viewScript(entry)}>
                        <Eye size={16} />
                        {t('history.trigger.changeScriptGenerated.viewScript')}
                      </button>
                    )}{' '}
                    <button type="button" disabled={readOnly} onClick={() => setPendingRevert(entry)}>
                      <Undo2 size={16} />
                      {t('history.revert.action')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {entries && entries.length >= 2 && (
          <div className="history-diff">
            <h3>{t('history.diff.title')}</h3>
            <div className="row">
              <div className="field">
                <label htmlFor="history-diff-from">{t('history.diff.from')}</label>
                <select id="history-diff-from" value={fromId} onChange={(event) => setFromId(event.target.value)}>
                  {entries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {formatDateTime(entry.timestamp, t, language)} — {t(TRIGGER_KEYS[entry.trigger])}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="history-diff-to">{t('history.diff.to')}</label>
                <select id="history-diff-to" value={toId} onChange={(event) => setToId(event.target.value)}>
                  {entries.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {formatDateTime(entry.timestamp, t, language)} — {t(TRIGGER_KEYS[entry.trigger])}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" disabled={diffBusy || !fromId || !toId || fromId === toId} onClick={() => void showDiff()}>
                <GitCompare size={16} />
                {t('history.diff.show')}
              </button>
            </div>

            {diffError && <div className="notice notice--error">{diffError}</div>}

            {diff && (
              diff.diff.length === 0 ? (
                <p className="hint">{t('history.diff.empty')}</p>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>{t('history.diff.column.object')}</th>
                      <th>{t('history.diff.column.path')}</th>
                      <th>{t('history.diff.column.old')}</th>
                      <th>{t('history.diff.column.new')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.diff.map((item, index) => (
                      <tr key={`${item.path}-${index}`} className={`diff-row diff-row--${item.changeType}`}>
                        <td>
                          <span className="badge">{t(DIFF_OBJECT_TYPE_KEYS[item.objectType])}</span>
                          <div className="hint">{t(DIFF_CHANGE_TYPE_KEYS[item.changeType])}</div>
                        </td>
                        <td>{item.path}</td>
                        <td>{item.oldValue ?? '—'}</td>
                        <td>{item.newValue ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            )}
          </div>
        )}
      </div>

      {pendingRevert && (
        <Modal
          title={t('history.revert.confirmTitle')}
          onClose={() => setPendingRevert(null)}
          actions={
            <>
              <button type="button" onClick={() => setPendingRevert(null)}>
                <X size={16} />
                {t('common.cancel')}
              </button>
              <button type="button" className="danger" disabled={revertBusy} onClick={() => void confirmRevert()}>
                <Undo2 size={16} />
                {t('history.revert.confirm')}
              </button>
            </>
          }
        >
          <div className="stack">
            <p>
              {t('history.revert.confirmQuestion', {
                time: formatDateTime(pendingRevert.timestamp, t, language),
              })}
            </p>
            <p className="hint">{t('history.revert.confirmHint')}</p>
          </div>
        </Modal>
      )}

      {viewingScript && (
        <Modal
          title={t('history.changeScript.title')}
          onClose={() => setViewingScript(null)}
          wide
          actions={
            <button type="button" onClick={() => setViewingScript(null)}>
              <X size={16} />
              {t('common.close')}
            </button>
          }
        >
          <div className="stack">
            {viewingScript.script === null && viewingScript.error === null && <p className="hint">{t('common.loading')}</p>}
            {viewingScript.error && <div className="notice notice--error">{viewingScript.error}</div>}
            {viewingScript.script !== null && <SqlPreview sql={viewingScript.script} />}
          </div>
        </Modal>
      )}
    </Modal>
  );
}
